<template>
  <div class="bc-row rowBorder">

    <!-- Name column -->
    <div :class="isDraft ? 'bc-col-name--draft' : 'bc-col-name'">
      <div class="broadcast-name-column">
        <template v-if="!showFullChannel">
          <img v-if="broadcast.img" :src="broadcast.img" :alt="`${broadcast.channel} logo`" class="channelImg" />
          <img v-else-if="broadcast.channel && channels?.[broadcast.channel]" :src="channels[broadcast.channel].icon" :alt="`${broadcast.channel} logo`" class="channelImg" />
        </template>
        <div class="name-wrapper">
          <p class="ion-no-margin title overflowTitle">{{ broadcast.name }}</p>
          <div class="id-wrapper sub-title" @click="copyToClipboard(broadcast.id || broadcast.broadcast_id)">
            ID: <p class="ion-no-margin overflowTitle">{{ broadcast.id || broadcast.broadcast_id }}</p>
            <VIcon style="cursor: pointer; color: var(--ion-color-primary)" slot="start" name="copy-2-line" class="icon"></VIcon>
          </div>
        </div>
      </div>
    </div>

    <!-- Show Full Channel -->
    <div v-if="showFullChannel && broadcast.channel && channels?.[broadcast.channel]" class="bc-col-channel">
      <div class="broadcast-name-column">
        <img :src="channels[broadcast.channel].icon" :alt="`${broadcast.channel} logo`" class="channelImg" />
        <p class="ion-no-margin title overflowTitle">{{ channels[broadcast.channel].name }}</p>
      </div>
    </div>

    <!-- Created On -->
    <div v-if="showTimestamps" :class="isDraft ? 'bc-col-date--draft' : 'bc-col-date'">
      <div class="date-text">
        <span class="title customPadding">{{ broadcast.createdOnFormatted || (broadcast.created_on ? moment(broadcast.created_on).format('DD MMM YYYY HH:mm') : '') }}</span>
        <span v-if="userTimezone" class="title">{{ '(' + userTimezone + ')' }}</span>
      </div>
    </div>

    <!-- Executed On (broadcasts only) -->
    <div v-if="!isDraft && showTimestamps" class="bc-col-date">
      <div class="date-text">
        <span class="title customPadding">{{ broadcast.executionDate || (broadcast.executed_on ? moment(broadcast.executed_on).format('DD MMM YYYY HH:mm') : '') }}</span>
        <span v-if="broadcast.executionTz" class="title">{{ '(' + broadcast.executionTz + ')' }}</span>
      </div>
    </div>

    <!-- Status (broadcasts only) -->
    <div v-if="!isDraft && showStatus" class="bc-col-status">
      <div v-if="broadcast.sent_status === 'done'" class="text-status-wrapper">
        <VIcon name="check-circle-line" class="checkmarkIcon" />
        Completed
      </div>
      <div v-else class="text-status-wrapper">
        <VIcon
          :name="statusMapping[broadcast.sent_status] === 'Pending' ? 'clock-line' : 'sand-clock-line'"
          :connotation="statusMapping[broadcast.sent_status] === 'Pending' ? undefined : 'cta'"
          :class="statusMapping[broadcast.sent_status] === 'Pending' ? 'pendingIcon' : 'otherStatusIcon'"
        />
        {{ statusMapping[broadcast.sent_status] || broadcast.sent_status }}
      </div>
    </div>

    <template v-if="showActions">
      <!-- Desktop Actions -->
      <div class="bc-col-actions">
        <div class="action-buttons-wrapper">

          <!-- Broadcast-specific desktop actions -->
          <template v-if="!isDraft">
            <span class="tooltip_container">
              <button
                v-if="broadcast.isVisibleBroadcastOutcomeButton"
                @click="emit('download-outcome', broadcast.id || broadcast.broadcast_id)"
                class="icon-btn"
              >
                <VIcon name="download-line" :size="-6"></VIcon>
              </button>
              <span class="tooltip_text left" style="width: 220px">Download broadcast outcome csv</span>
            </span>
            <span class="tooltip_container">
              <p
                v-if="
                  broadcast.targeting_count &&
                  broadcast.targeting_count != null &&
                  broadcast.sent_status == 'done' &&
                  ['whatsapp', 'googlercs', 'sms'].includes(broadcast.channel) &&
                  !broadcast.isVisibleBroadcastOutcomeButton
                "
                class="sub-title"
              >
                Generating report...
              </p>
            </span>
            <span v-if="['running', 'done', 'loading'].includes(broadcast.sent_status)" class="tooltip_container">
              <button class="icon-btn" @click="emit('view-edit', { index, isDraft: false })">
                <VIcon name="eye-line" :size="-6"></VIcon>
              </button>
              <span class="tooltip_text left" style="width: 100px">View Details</span>
            </span>
            <span v-else class="tooltip_container">
              <button class="icon-btn" @click="emit('view-edit', { index, isDraft: false })">
                <VIcon name="edit-line" :size="-6"></VIcon>
              </button>
              <span class="tooltip_text left" style="width: 80px">Edit Details</span>
            </span>
            <span class="tooltip_container">
              <button
                @click="emit('delete', index)"
                :disabled="!broadcast.isEnabledDeleteButton"
                class="icon-btn trashIcon"
              >
                <VIcon name="delete-line" connotation="alert" slot="start" :size="-6"></VIcon>
              </button>
              <span class="tooltip_text left tooltip-width">Delete</span>
            </span>
            <ActionPopOver :style="{ '--width': '215px' }">
              <div class="listContainer">
                <VButton appearance="outlined" connotation="cta" label="Duplicate" @click="emit('duplicate', { index, isDraft: false })"></VButton>
              </div>
            </ActionPopOver>
          </template>

          <!-- Draft-specific desktop actions -->
          <template v-else>
            <span class="tooltip_container">
              <button class="icon-btn" @click="emit('view-edit', { index, isDraft: true })">
                <VIcon name="edit-line" :size="-6"></VIcon>
              </button>
              <span class="tooltip_text left" style="width: 80px">Edit Details</span>
            </span>
            <span class="tooltip_container">
              <button class="icon-btn trashIcon" @click="emit('delete', index)">
                <VIcon name="delete-line" connotation="alert" slot="start" :size="-6"></VIcon>
              </button>
              <span class="tooltip_text left tooltip-width">Delete</span>
            </span>
            <ActionPopOver :style="{ '--width': '215px' }">
              <div class="listContainer">
                <VButton appearance="outlined" connotation="cta" label="Duplicate" @click="emit('duplicate', { index, isDraft: true })"></VButton>
              </div>
            </ActionPopOver>
          </template>

        </div>
      </div>

      <!-- Mobile Action Popover -->
      <div class="bc-col-actions-mobile">
        <ActionPopOver :style="{ '--width': '215px' }">
          <div class="listContainer">

            <!-- Broadcast-specific mobile actions -->
            <template v-if="!isDraft">
              <span
                v-if="
                  broadcast.targeting_count &&
                  broadcast.targeting_count != null &&
                  broadcast.sent_status == 'done' &&
                  ['whatsapp', 'googlercs', 'sms'].includes(broadcast.channel) &&
                  !broadcast.isVisibleBroadcastOutcomeButton
                "
              >
                <p class="sub-title">Generating report...</p>
              </span>
              <VButton
                v-if="broadcast.isVisibleBroadcastOutcomeButton"
                @click="emit('download-outcome', broadcast.id || broadcast.broadcast_id)"
                appearance="outlined" connotation="cta"
                :label="broadcast.outcomeLoading ? 'Downloading...' : 'Broadcast outcome'"
              ></VButton>
              <VButton
                v-if="['running', 'done'].includes(broadcast.sent_status)"
                @click="emit('view-edit', { index, isDraft: false })"
                appearance="outlined" connotation="cta" label="View"
              ></VButton>
              <VButton
                v-else
                @click="emit('view-edit', { index, isDraft: false })"
                appearance="outlined" connotation="cta" label="Edit"
              ></VButton>
              <VButton appearance="outlined" connotation="cta" label="Duplicate" @click="emit('duplicate', { index, isDraft: false })"></VButton>
              <VButton
                :disabled="!broadcast.isEnabledDeleteButton"
                @click="emit('delete', index)"
                appearance="outlined" connotation="cta" label="Delete"
              ></VButton>
            </template>

            <!-- Draft-specific mobile actions -->
            <template v-else>
              <VButton appearance="outlined" connotation="cta" label="Edit" @click="emit('view-edit', { index, isDraft: true })"></VButton>
              <VButton appearance="outlined" connotation="cta" label="Duplicate" @click="emit('duplicate', { index, isDraft: true })"></VButton>
              <VButton appearance="outlined" connotation="cta" label="Delete" @click="emit('delete', index)"></VButton>
            </template>

          </div>
        </ActionPopOver>
      </div>
    </template>

  </div>
</template>

<script setup>
import ActionPopOver from '@/components/ActionPopOver.vue';
import { useClipboard } from '@/composables/useClipboard';

const { copyToClipboard } = useClipboard();

const timeOutline = 'clock-line';
import { channels } from '@/resources/js/bot-builder';
import moment from 'moment';
import { useUserData } from '@/composables/useUserData';
import { ref, computed } from 'vue';

defineProps({
  broadcast: { type: Object, required: true },
  index: { type: Number, required: true },
  isDraft: { type: Boolean, default: false },
  showActions: { type: Boolean, default: true },
  showFullChannel: { type: Boolean, default: false },
  showTimestamps: { type: Boolean, default: true },
  showStatus: { type: Boolean, default: true },
});

const emit = defineEmits(['view-edit', 'delete', 'duplicate', 'download-outcome']);

const statusMapping = ref({
  not_ran_yet: "Pending",
  running: "In progress"
});

const { user } = useUserData();
const userTimezone = computed(() => user.value?.timezone || '');
</script>

<style scoped>
.broadcast-name-column {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  width: 100%;
}

.name-wrapper {
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.id-wrapper {
  display: flex;
  gap: 3px;
  font-size: 12px;
  align-items: center;
  cursor: pointer;
  margin-top: 3px;
}

.action-buttons-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.text-status-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-start;
}

.date-text {
  display: flex;
  flex-wrap: wrap;
  column-gap: 45px;
  flex-direction: column;
}

.customPadding {
  padding-bottom: 5px;
}

.channelImg {
  width: 20px;
  height: auto;
}

.rowBorder {
  border-bottom: 1px solid var(--background-primary);
  padding: 15px 20px 15px 20px;
}

.rowBorder:last-child {
  border-bottom: none;
}

.checkmarkIcon {
  color: var(--green);
}

.otherStatusIcon {
  color: var(--purple);
}

.pendingIcon {
  color: var(--gray-04);
}

.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
}
.icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.listContainer {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding: 10px;
  gap: 10px;
}
</style>
